import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Briefcase, MapPin, Clock, DollarSign, Building2, Calendar, CheckCircle, Bookmark, BookmarkCheck, ArrowLeft } from "lucide-react";

const OpportunityDetail = () => {
  const { token } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [application, setApplication] = useState({
    resume_link: "",
    cover_letter: ""
  });

  useEffect(() => {
    fetchOpportunity();
  }, [id]);

  const fetchOpportunity = async () => {
    try {
      const response = await axios.get(`${API}/opportunities/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOpportunity(response.data);
    } catch (error) {
      console.error("Error loading opportunity:", error);
      toast.error("Failed to load opportunity details");
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/opportunities/${id}/apply`,
        application,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowApplyModal(false);
      fetchOpportunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit application");
    } finally {
      setApplying(false);
    }
  };

  const handleSave = async () => {
    try {
      if (opportunity.is_saved) {
        await axios.delete(`${API}/opportunities/${id}/save`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Opportunity removed from saved");
      } else {
        await axios.post(`${API}/opportunities/${id}/save`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Opportunity saved successfully");
      }
      fetchOpportunity();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save opportunity");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      reviewed: "bg-blue-100 text-blue-700",
      shortlisted: "bg-purple-100 text-purple-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700"
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-center text-gray-500">Opportunity not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate("/opportunities")} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Opportunities
        </Button>

        {/* Header Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-3">
                  <Badge>{opportunity.field}</Badge>
                  <Badge className={opportunity.work_mode === "remote" ? "bg-green-100 text-green-700" : opportunity.work_mode === "hybrid" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}>
                    {opportunity.work_mode}
                  </Badge>
                  <Badge className={opportunity.type === "internship" ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}>
                    {opportunity.type.toUpperCase()}
                  </Badge>
                </div>
                <CardTitle className="text-3xl mb-2">{opportunity.title}</CardTitle>
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    <span className="font-semibold text-lg">{opportunity.company_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    <span>{opportunity.location}</span>
                  </div>
                </div>
              </div>
              <Button variant="outline" onClick={handleSave}>
                {opportunity.is_saved ? (
                  <>
                    <BookmarkCheck className="w-5 h-5 mr-2" />
                    Saved
                  </>
                ) : (
                  <>
                    <Bookmark className="w-5 h-5 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {opportunity.type === "internship" && opportunity.stipend && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-indigo-500" />
                  <div>
                    <p className="text-sm text-gray-600">Stipend</p>
                    <p className="font-semibold">{opportunity.stipend}</p>
                  </div>
                </div>
              )}
              
              {opportunity.type === "job" && opportunity.salary && (
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-indigo-500" />
                  <div>
                    <p className="text-sm text-gray-600">Salary</p>
                    <p className="font-semibold">{opportunity.salary}</p>
                  </div>
                </div>
              )}

              {opportunity.duration && (
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-500" />
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="font-semibold">{opportunity.duration}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="text-sm text-gray-600">Deadline</p>
                  <p className="font-semibold">{new Date(opportunity.application_deadline).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-indigo-500" />
                <div>
                  <p className="text-sm text-gray-600">Experience</p>
                  <p className="font-semibold capitalize">{opportunity.experience_level}</p>
                </div>
              </div>
            </div>

            {/* Application Status */}
            {opportunity.has_applied && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-500" />
                  <p className="font-semibold">You have applied to this opportunity</p>
                  <Badge className={getStatusColor(opportunity.application_status)}>
                    {opportunity.application_status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            )}

            {/* Apply Button */}
            {!opportunity.has_applied && opportunity.status === "active" && (
              <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
                <DialogTrigger asChild>
                  <Button className="w-full md:w-auto" size="lg">
                    Apply Now (+5 points)
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Apply for {opportunity.title}</DialogTitle>
                    <DialogDescription>
                      Submit your application. You'll earn 5 points for applying!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="resume_link">Resume Link (Optional)</Label>
                      <Input
                        id="resume_link"
                        placeholder="https://drive.google.com/..."
                        value={application.resume_link}
                        onChange={(e) => setApplication({...application, resume_link: e.target.value})}
                      />
                      <p className="text-sm text-gray-500 mt-1">Link to your resume (Google Drive, Dropbox, etc.)</p>
                    </div>
                    <div>
                      <Label htmlFor="cover_letter">Cover Letter (Optional)</Label>
                      <Textarea
                        id="cover_letter"
                        placeholder="Tell us why you're interested in this opportunity..."
                        rows={6}
                        value={application.cover_letter}
                        onChange={(e) => setApplication({...application, cover_letter: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setShowApplyModal(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleApply} disabled={applying}>
                      {applying ? "Submitting..." : "Submit Application"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>

        {/* Description */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>About this {opportunity.type}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 whitespace-pre-line">{opportunity.description}</p>
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
          </CardHeader>
          <CardContent>
            {opportunity.requirements ? (
              typeof opportunity.requirements === 'string' ? (
                <div className="whitespace-pre-line text-gray-700">
                  {opportunity.requirements}
                </div>
              ) : (
                <ul className="space-y-2">
                  {opportunity.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <p className="text-gray-500">No requirements specified</p>
            )}
          </CardContent>
        </Card>

        {/* Responsibilities */}
        {opportunity.responsibilities && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Responsibilities</CardTitle>
            </CardHeader>
            <CardContent>
              {typeof opportunity.responsibilities === 'string' ? (
                <div className="whitespace-pre-line text-gray-700">
                  {opportunity.responsibilities}
                </div>
              ) : Array.isArray(opportunity.responsibilities) && opportunity.responsibilities.length > 0 ? (
                <ul className="space-y-2">
                  {opportunity.responsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <span>{resp}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No responsibilities specified</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Skills Required */}
        {opportunity.skills_required && opportunity.skills_required.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Skills Required</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {opportunity.skills_required.map((skill, idx) => (
                  <Badge key={idx} variant="secondary">{skill}</Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OpportunityDetail;
